import {useMemo} from 'react'
import {useQueries} from '@tanstack/react-query'
import {useAccessToken, useCommerceApi} from '@salesforce/commerce-sdk-react'

const CHUNK_SIZE = 24

/**
 * Fetches products in parallel chunks to work around the SCAPI 24-item limit.
 * Returns the same {data, isLoading} shape as useProducts, where data is an id-keyed map.
 *
 * @param {string} productIds - Comma-separated product IDs
 * @param {Object} queryOptions - React Query options (enabled, staleTime, etc.)
 * @param {Object} [queryOptions.parameters] - Additional/override SCAPI getProducts parameters
 *   (e.g. `select`, `expand`). Merged over the defaults; `ids` is always managed internally.
 */
export const useProductsChunked = (productIds, queryOptions = {}) => {
    const api = useCommerceApi()
    const {getTokenWhenReady} = useAccessToken()
    const {enabled = true, staleTime, parameters = {}} = queryOptions

    const idChunks = useMemo(() => {
        if (!productIds) return []
        const ids = productIds.split(',').filter(Boolean)
        const chunks = []
        for (let i = 0; i < ids.length; i += CHUNK_SIZE) {
            chunks.push(ids.slice(i, i + CHUNK_SIZE))
        }
        return chunks
    }, [productIds])

    // Stable, serializable representation of the extra params so it can be used
    // as part of the query key (for correct caching).
    const parametersKey = JSON.stringify(parameters)

    const queries = useQueries({
        queries: idChunks.map((chunk) => ({
            queryKey: ['products', chunk.join(','), parametersKey],
            queryFn: async () => {
                const accessToken = await getTokenWhenReady()
                return api.shopperProducts.getProducts({
                    parameters: {
                        allImages: true,
                        perPricebook: true,
                        ...parameters,
                        ids: chunk.join(',')
                    },
                    headers: accessToken
                        ? {
                              Authorization: `Bearer ${accessToken}`
                          }
                        : undefined
                })
            },
            enabled: enabled && chunk.length > 0,
            ...(staleTime !== undefined && {staleTime})
        }))
    })

    const isLoading = idChunks.length > 0 && queries.some((q) => q.isLoading)

    const data = useMemo(() => {
        if (idChunks.length === 0 || queries.some((q) => q.isLoading)) return undefined
        return queries.reduce((acc, query) => {
            const items = query.data?.data ?? []
            items.forEach((item) => {
                acc[item.id] = item
            })
            return acc
        }, {})
    }, [queries, idChunks])

    return {data, isLoading}
}
